import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, X, Loader2, Send } from 'lucide-react';
import { api, formatApiErrorDetail } from '../lib/api';
import { toast } from 'sonner';

/**
 * CommentsDrawer — a right-edge drawer keyed on (targetType, targetId).
 * Pass `open` + `onClose` from the parent; pass `label` for the header.
 * Works with the existing /api/comments backend.
 */
export function CommentsDrawer({ open, onClose, targetType, targetId, label, scenarioId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || !targetId) return;
    setLoading(true);
    api.get('/comments', { params: { target_type: targetType, target_id: targetId } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, targetType, targetId]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 100); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post('/comments', {
        target_type: targetType, target_id: targetId, text: body, scenario_id: scenarioId,
      });
      setRows((prev) => [data, ...prev]);
      setText('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="comments-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-full max-w-md h-screen bg-white shadow-2xl flex flex-col">
        <header className="border-b border-indie-navy/10 px-6 py-4 flex items-start gap-3">
          <MessageSquare size={18} className="text-indie-evergreen mt-0.5" />
          <div className="flex-1">
            <div className="label-editorial">Comments</div>
            <div className="text-base font-semibold text-indie-navy">{label}</div>
            <div className="text-[11px] text-indie-ink/50 font-mono mt-0.5">{targetType} · {targetId}</div>
          </div>
          <button onClick={onClose} className="text-indie-ink/60 hover:text-indie-navy" data-testid="close-comments">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading && <div className="flex items-center gap-2 text-sm text-indie-ink/60"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-indie-ink/60 leading-relaxed py-8 text-center">
              No comments yet. Leave a note for your future self or a teammate — they'll see it the next time they look at this metric.
            </div>
          )}
          {rows.map((c) => (
            <div key={c.id} className="border-l-2 border-indie-evergreen pl-3">
              <div className="flex items-center gap-2 text-xs text-indie-ink/60">
                <span className="font-semibold text-indie-navy">{c.user_name || c.user_email}</span>
                <span>·</span>
                <span>{new Date(c.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </div>
              <div className="mt-1 text-sm text-indie-ink whitespace-pre-wrap leading-relaxed">{c.text}</div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="border-t border-indie-navy/10 p-4 bg-indie-cream/40">
          <textarea
            ref={inputRef}
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Leave a note…"
            className="w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm focus:outline-none focus:border-indie-evergreen"
            data-testid="comment-input"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={sending || !text.trim()}
              className="flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-4 py-2 hover:bg-indie-yellow/85 disabled:opacity-50"
              data-testid="submit-comment"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Post
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

/**
 * CommentButton — small icon-button to attach to any KPI/line. Shows a count
 * badge when comments exist, opens the drawer on click.
 */
export function CommentButton({ targetType, targetId, label, scenarioId, className = '' }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!targetId) return;
    api.get('/comments', { params: { target_type: targetType, target_id: targetId } })
      .then((r) => setCount((r.data || []).length))
      .catch(() => setCount(0));
  }, [targetType, targetId, open]);

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label={`Comments for ${label}`}
        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-indie-ink/50 hover:text-indie-evergreen transition-colors ${className}`}
        data-testid={`comment-btn-${targetId}`}
      >
        <MessageSquare size={12} />
        {count ? <span className="font-semibold">{count}</span> : null}
      </button>
      <CommentsDrawer
        open={open}
        onClose={() => setOpen(false)}
        targetType={targetType}
        targetId={targetId}
        label={label}
        scenarioId={scenarioId}
      />
    </>
  );
}
